<template>
	<div class="price">
		<span
			class="price__value"
			:class="[
				`currency-${currency}`,
				{
					'price--negative':
						!hasEnoughResources && currency !== ResourceType.GEMS,
					'price--value-positive':
						resourceChangeHighlight ===
						ResourceChangeHighlight.POSITIVE,
					'price--value-negative':
						resourceChangeHighlight ===
						ResourceChangeHighlight.NEGATIVE,
				},
			]"
			:style="{
				lineHeight: `${fontSize / 16}rem`,
				fontWeight: fontWeight,
			}"
		>
			<ShrinkText
				:text="
					(sign && !percentageBonus
						? sign === ResourceChangeHighlight.POSITIVE
							? '+'
							: '-'
						: '') +
					formatNumber(amount, true) +
					(postfix || '')
				"
				:fontSize="fontSize"
				:forcedFontSize="!allowShrink ? fontSize : null"
				:isBold="fontWeight === FontWeights.BOLD ? true : false"
			/>
		</span>
		<span
			v-if="percentageBonus"
			class="price__bonus"
			:style="{
				lineHeight: `${fontSize / 16}rem`,
				fontWeight: fontWeight,
			}"
		>
			<ShrinkText
				:text="
					(amount < 0 ? '-' : '+') +
					formatNumber(
						roundNumber(Math.abs(amount) * (percentageBonus / 100)),
					) +
					(postfix || '')
				"
				:fontSize="fontSize"
				:forcedFontSize="!allowShrink ? fontSize : null"
				:isBold="fontWeight === FontWeights.BOLD ? true : false"
			/>
		</span>
		<AppMainIcon
			v-if="currency"
			:iconName="currency"
			:iconSize="iconSize"
			:style="{ marginTop: `${fontSize / 8 / 16}rem` }"
		/>
	</div>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import { ResourceType } from "@/game/entities/config/resources";
import useResourcesStore from "@/store/useResourcesStore";
import { FontWeights, ResourceChangeHighlight } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { roundNumber } from "@/utils/number";
import { storeToRefs } from "pinia";
import { computed } from "vue";

const resourcesStore = useResourcesStore();
const resources = storeToRefs(resourcesStore);

const getCurrencyValue = (currency: ResourceType | string): number => {
	switch (currency) {
		case ResourceType.FOOD_DIFF:
			return resources.foodDiff?.value ?? 0;
		case ResourceType.POPULATION_DIFF:
			return resources.populationDiff?.value ?? 0;
		case ResourceType.COINS:
			return resources.coins?.value ?? 0;
		case ResourceType.GEMS:
			return resources.gems?.value ?? 0;
		case ResourceType.LUMBER:
			return resources.lumber?.value ?? 0;
		case ResourceType.CLAY:
			return resources.clay?.value ?? 0;
		case ResourceType.STONE:
			return resources.stone?.value ?? 0;
		case ResourceType.GOODS:
			return resources.goods?.value ?? 0;
		case ResourceType.IRON:
			return resources.iron?.value ?? 0;
		case ResourceType.STEEL:
			return resources.steel?.value ?? 0;
		case ResourceType.ARMY:
			return resources.army?.value ?? 0;
		case ResourceType.RATIONS:
			return resources.rations?.value ?? 0;
		case ResourceType.EVENTIUM:
			return resources.eventium?.value ?? 0;
		default:
			return 0;
	}
};

const hasEnoughResources = computed((): boolean => {
	if (!resourceCheck) return true;
	if (!currency) return true;
	return getCurrencyValue(currency) >= amount;
});

interface Props {
	amount: number;
	currency?: ResourceType | null;
	resourceCheck?: boolean;
	resourceChangeHighlight?: ResourceChangeHighlight | null;
	resourceChangeSign?: ResourceChangeHighlight | null;
	iconSize?: number;
	fontSize?: number; // in px
	fontWeight?: FontWeights;
	percentageBonus?: number | null;
	allowShrink?: boolean;
	postfix?: string | null;
}

const {
	amount = 0,
	currency = null,
	resourceCheck = true, // NOTE: standardly check for enough resources
	resourceChangeHighlight = null,
	resourceChangeSign = null,
	iconSize = 20,
	fontSize = 16,
	fontWeight = FontWeights.BOLD, // NOTE: default: bold
	percentageBonus = null,
	allowShrink = false,
	postfix = null,
} = defineProps<Props>();

const sign = computed((): ResourceChangeHighlight | null => {
	return resourceChangeSign === null
		? resourceChangeHighlight
		: resourceChangeSign;
});
</script>

<style lang="scss" scoped>
.price {
	@include flex-row-center;
	gap: 0.25rem;
	margin: 0 0.2rem;

	span {
		color: #f2dcb5;
		line-height: 1rem;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;

		&.price--negative {
			color: #d1080f;
		}

		&.price--value-positive {
			color: #19b345;
		}

		&.price--value-negative {
			color: #ff3d3d;
		}
	}

	.price__value,
	.price__bonus {
		flex: 0 1 auto; // IMPORTANT: we don't want to grow, maximally allow wrappers to shape the text size thanks to shrink
		overflow: hidden;
	}

	.icon {
		flex-shrink: 0;
		filter: drop-shadow(0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6));
	}
}
</style>
